import React, { Suspense, lazy, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../styles/MainPage.css';
import {
  ProfileBookingProvider,
} from '../ProfileBookingContext';
import { patchAdminSearch } from '../../../utils/adminDashboardNav';

import History from './History';

const Notes = lazy(() => import('./Notes'));
const Email = lazy(() => import('./Email'));
const Call = lazy(() => import('./Call'));
const Meeting = lazy(() => import('./Meeting'));
const Service = lazy(() => import('./Service'));
const Invoice = lazy(() => import('./Invoice'));
const Files = lazy(() => import('./Files'));
const Quotation = lazy(() => import('./Quotation'));
const ProjectManager = lazy(() => import('./ProjectManager'));
const Chat = lazy(() => import('./Chat'));

const TAB_ITEMS = [
  { name: 'Activity', Component: History },
  { name: 'Notes', Component: Notes },
  { name: 'Emails', Component: Email },
  { name: 'Calls', Component: Call },
  { name: 'Meetings', Component: Meeting },
  { name: 'Chat', Component: Chat },
  { name: 'Quotations', Component: Quotation },
  { name: 'Services', Component: Service },
  { name: 'Projects', Component: ProjectManager },
  { name: 'Invoices', Component: Invoice },
  { name: 'Files', Component: Files },
];

function tabFromSearch(search) {
  const raw = new URLSearchParams(String(search || '').replace(/^\?/, '')).get(
    'tab',
  );
  return TAB_ITEMS.some((tab) => tab.name === raw) ? raw : 'Activity';
}

function ProfileTabs({ bookingId }) {
  const location = useLocation();
  const navigate = useNavigate();
  const urlTab = tabFromSearch(location.search);
  const [activeTab, setActiveTab] = useState(urlTab);
  const [mountedTabs, setMountedTabs] = useState(
    () => new Set(['Activity', urlTab]),
  );

  useEffect(() => {
    setActiveTab(urlTab);
    setMountedTabs((prev) => {
      if (prev.has(urlTab)) return prev;
      const next = new Set(prev);
      next.add(urlTab);
      return next;
    });
  }, [urlTab]);

  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
    setMountedTabs((prev) => {
      if (prev.has(tabName)) return prev;
      const next = new Set(prev);
      next.add(tabName);
      return next;
    });
    const remove = [];
    if (tabName !== 'Quotations') remove.push('quote', 'action');
    if (tabName !== 'Invoices') remove.push('invoice');
    if (tabName !== 'Projects') remove.push('project');
    if (tabName !== 'Emails') remove.push('emailTab', 'emailAction');
    if (tabName !== 'Quotations' && tabName !== 'Invoices') remove.push('mode');
    const currentSearch =
      typeof window !== 'undefined' ? window.location.search : location.search;
    const currentParams = new URLSearchParams(
      String(currentSearch || '').replace(/^\?/, ''),
    );
    const patch = {
      tab: tabName === 'Activity' ? '' : tabName,
    };
    if (bookingId) patch.bookingId = bookingId;
    const page = currentParams.get('page');
    const pipelineId = currentParams.get('pipelineId');
    if (page) patch.page = page;
    if (pipelineId) patch.pipelineId = pipelineId;
    const search = patchAdminSearch(currentSearch, patch, remove);
    if (search !== (currentSearch || '')) {
      navigate({ pathname: location.pathname || '/', search }, { replace: true });
    }
  };

  return (
    <div className="main-page">
      <div className="tabs-header">
        {TAB_ITEMS.map((tab) => (
          <button
            key={tab.name}
            type="button"
            className={`tab-btn ${activeTab === tab.name ? 'active' : ''}`}
            onClick={() => handleTabChange(tab.name)}
          >
            {tab.name}
          </button>
        ))}
      </div>

      <div className="tabs-content">
        <Suspense fallback={<p className="tab-loading">Loading…</p>}>
          {TAB_ITEMS.map(({ name, Component }) =>
            mountedTabs.has(name) ? (
              <div
                key={name}
                className="tab-pane"
                hidden={activeTab !== name}
              >
                <Component bookingId={bookingId} />
              </div>
            ) : null,
          )}
        </Suspense>
      </div>
    </div>
  );
}

export default function MainPage({ bookingId, lead }) {
  return (
    <ProfileBookingProvider bookingId={bookingId} lead={lead}>
      <ProfileTabs bookingId={bookingId} />
    </ProfileBookingProvider>
  );
}
